@if (!windowService.isDesktopRes) {
  <tidy-top-nav
    class="mobile"
    [title]="dayTitle"
    [subtitle]="daySubtitle"
    [canGoBack]="true"
    [customBack]="'optimizer'"
    [showLinkIcon]="false" />
}

<ng-template #ddContent>
  <div class="dd-body">

  @if (ready) {

  <!-- Leased day: the signed lease is what's actually holding the calendar,
       so it leads the panel with a compact read-only card and the rest of
       the pipeline (availability pills, block-off checkbox, tier switcher,
       pricing cards) is suppressed, same as a fully-booked day. -->
  @if (leaseVm) {
    <section class="rc-section">
      <article class="rc-layer" data-layer="lease">
        <div class="rc-layer__head">
          <tidy-text [header]="'Lease'" class="rc-layer__name" />
        </div>
        <div class="rc-layer__body">
          <div class="rc-running">
            <tidy-text [title]="leaseVm.tenantName" class="rc-running__amount" />
            <tidy-text [helper]="leaseVm.monthlyRentLabel" class="rc-running__detail" />
          </div>
          <tidy-text [body]="leaseVm.termLabel" />
          <tidy-text
            class="white-link"
            [actionBody]="'View lease ledger'"
            (action)="goToLeaseLedger()" />
        </div>
      </article>
    </section>
  }

  <!-- Single-tier listings hide the "Available for" card and tier switcher
       entirely (nothing to switch between), so blocking needs its own
       lightweight control up top instead of the pills.
       Demo-only: the pills and tier switcher are backed by the mock
       rental-strategy service, so real accounts skip straight to the pipeline
       and block dates from the Layer 1 checkbox instead. -->
  @if (!isRealMode && singleTier && !leaseVm) {
    <div class="rc-block-toggle">
      <tidy-checkbox
        class="rc-block-check"
        [initValue]="isBlocked"
        [checked]="isBlocked"
        [disabled]="!selectedAvailable"
        (checkedChange)="toggleBlockOff($event)"
        [items]="blockOffCheckboxItems" />
    </div>
  }

  @if (!isRealMode && !singleTier && !leaseVm) {

  <!-- 1. Available for — replaces the old reserved hero/banner + block-off
       checkbox with a single availability card that always leads the panel. -->
  <section class="rc-section">
    <article class="rc-layer" data-layer="1">
      <div class="rc-layer__head">
        <span class="rc-layer__num">1</span>
        <tidy-text [header]="'Available for'" class="rc-layer__name" />
      </div>
      <div class="rc-layer__body">
        @if (!selectedAvailable) {
          <tidy-text [helper]="'This date is already booked.'" class="rc-avail-note" />
        }
        <div class="rc-avail-pills">
          <button
            type="button"
            class="rc-avail-pill"
            [class.rc-avail-pill--on]="availableShort"
            [disabled]="!selectedAvailable"
            (click)="toggleAvailability('short')">
            @if (availableShort) {
              <img class="rc-avail-pill__check" src="assets/svg/checkmark-outline.svg" alt="" />
            }
            <tidy-text [body]="'Short-term'" class="bold" />
          </button>
          <button
            type="button"
            class="rc-avail-pill"
            [class.rc-avail-pill--on]="availableLonger"
            [disabled]="!selectedAvailable"
            (click)="toggleAvailability('longer')">
            @if (availableLonger) {
              <img class="rc-avail-pill__check" src="assets/svg/checkmark-outline.svg" alt="" />
            }
            <tidy-text [body]="'Longer-term'" class="bold" />
          </button>
        </div>

        @if (activeCoveringWindow && activeCoveringIsMultiDay) {
          <div class="rc-avail-context">
            <tidy-text [helper]="('Part of ' | translate) + activeCoveringRangeLabel + (' hold' | translate)" class="rc-avail-context__label" />
            <tidy-text
              class="white-link rc-avail-context__remove"
              [actionBody]="'Remove'"
              (action)="removeCoveringWindow()" />
          </div>
        }

        @if (selectedAvailable) {
          @if (!rangeApplyOpen) {
            <tidy-text
              class="white-link rc-avail-range-link"
              [actionBody]="'Apply through a date range…'"
              (action)="openRangeApply()" />
          } @else {
            <form [formGroup]="rangeApplyForm" class="rc-avail-range-form">
              <tidy-datepicker
                [label]="'End date'"
                formControlName="endDate"
                [form]="rangeApplyForm.controls.endDate" />
              <div class="rc-avail-range-form__actions">
                <tidy-button [text]="'Cancel'" class="secondary" [action]="closeRangeApply.bind(this)" />
                <tidy-button [text]="'Apply'" class="primary" [action]="submitRangeApply.bind(this)" />
              </div>
            </form>
          }
        }
      </div>
    </article>
  </section>

  <!-- 2. Tier switcher — a tab whose tier is off for this date stays
       clickable (muted, with an "off" marker) so the user can still peek at
       what that tier's settings would look like. -->
  <section class="rc-section">
    <div class="rc-tier-tabs">
      <button
        type="button"
        class="rc-tier-tab"
        [class.rc-tier-tab--active]="viewTier === 'short'"
        [class.rc-tier-tab--off]="!availableShort"
        (click)="setViewTier('short')">
        <tidy-text [body]="'Short-term'" class="bold" />
        @if (!availableShort) {
          <span class="rc-tier-tab__off-marker">off</span>
        }
      </button>
      <button
        type="button"
        class="rc-tier-tab"
        [class.rc-tier-tab--active]="viewTier === 'longer'"
        [class.rc-tier-tab--off]="!availableLonger"
        (click)="setViewTier('longer')">
        <tidy-text [body]="'Longer-term'" class="bold" />
        @if (!availableLonger) {
          <span class="rc-tier-tab__off-marker">off</span>
        }
      </button>
    </div>
  </section>

  }

  @if (!leaseVm) {
  @if (viewTier === 'longer') {

    <!-- Longer-term view: compact, read-only, single card. -->
    <section class="rc-flow">
      <!-- Monthly target — numbered via cardNum.monthlyTarget (2 dual-tier / 1 single-tier). -->
      <article class="rc-layer" [attr.data-layer]="cardNum.monthlyTarget">
        <div class="rc-layer__head">
          <span class="rc-layer__num">{{ cardNum.monthlyTarget }}</span>
          <tidy-text [header]="'Monthly target'" class="rc-layer__name" />
        </div>
        <div class="rc-layer__body">
          @if (longerVm) {
            <div class="rc-ml-target-grid">
              <div class="rc-ml-target-col">
                <tidy-text [body]="'Mid-term'" class="rc-ml-target-col__title bold" />
                <div class="rc-running">
                  <tidy-text [title]="fmt(longerVm.midTargetCents)" class="rc-running__amount" />
                  <tidy-text [helper]="('Floor ' | translate) + fmt(longerVm.midFloorCents)" class="rc-running__detail" />
                </div>
                <tidy-text [helper]="'≈ ' + fmt(longerVm.midNightlyEquivCents) + ('/night equivalent' | translate)" />
              </div>
              <div class="rc-ml-target-col">
                <tidy-text [body]="'Long-term'" class="rc-ml-target-col__title bold" />
                <div class="rc-running">
                  <tidy-text [title]="fmt(longerVm.longTargetCents)" class="rc-running__amount" />
                  <tidy-text [helper]="('Floor ' | translate) + fmt(longerVm.longFloorCents)" class="rc-running__detail" />
                </div>
                <tidy-text [helper]="'≈ ' + fmt(longerVm.longNightlyEquivCents) + ('/night equivalent' | translate)" />
              </div>
            </div>
            <tidy-text
              class="white-link"
              [actionBody]="'Open Longer-term settings'"
              (action)="goToBookingSettings()" />
          }
        </div>
      </article>
    </section>

  } @else if (selectedAvailable) {

    <!-- Short-term view: the existing pipeline cards, numbered via cardNum
         (2-7 demo dual-tier / 1-6 single-tier and real accounts). -->
    <section class="rc-flow">
      <!-- Your Price Limits -->
      <article class="rc-layer" [attr.data-layer]="cardNum.priceLimits" [class.rc-layer--blocked]="isBlocked">
        <div class="rc-layer__head">
          <span class="rc-layer__num">{{ cardNum.priceLimits }}</span>
          <tidy-text [header]="'Your Price Limits'" class="rc-layer__name" />
        </div>
        <div class="rc-layer__body">
          <form [formGroup]="priceForm" class="rc-price-form">
            <div class="rc-price-limits">
              <div class="rc-price-pill">
                <tidy-text [body]="minPillLabel" class="rc-price-pill__amount" />
                <tidy-text [helper]="'Min price'" class="rc-price-pill__label" />
              </div>
              <div class="rc-price-pill">
                <tidy-text [body]="maxPillLabel" class="rc-price-pill__amount" />
                <tidy-text [helper]="'Max price'" class="rc-price-pill__label" />
              </div>
            </div>

            <tidy-checkbox
              class="rc-override-toggle"
              [initValue]="showOverride"
              [(checked)]="showOverride"
              (checkedChange)="onShowOverrideChange($event)"
              [items]="overrideToggleItems" />

            @if (showOverride) {
              <tidy-input
                [label]="'Manual price override'"
                formControlName="override"
                [form]="priceForm.get('override')"
                mask="currencyMask"
                currencySymbol="$"
                [icon]="'assets/svg/cash-outline.svg'"
                [submitted]="false" />
            }
          </form>

          <!-- Real accounts block a date here; demo accounts do it through the
               "Available for" pills (or the single-tier toggle) further up. -->
          @if (isRealMode) {
            <div class="rc-layer1-footer">
              <tidy-checkbox
                class="rc-block-check"
                [initValue]="blockedOff"
                [(checked)]="blockedOff"
                (checkedChange)="onBlockedOffChange($event)"
                [items]="blockOffCheckboxItems" />
            </div>
          }
        </div>
      </article>

      <!-- Suggested price -->
      <article class="rc-layer" [attr.data-layer]="cardNum.priceLabs"
        [class.rc-layer--inactive]="isBlocked || hasOverride">
        <div class="rc-layer__head">
          <span class="rc-layer__num">{{ cardNum.priceLabs }}</span>
          <tidy-text [header]="'Suggested price'" class="rc-layer__name" />
        </div>
        <div class="rc-layer__body">
          @if (hasSuggestion) {
            <div class="rc-running">
              <tidy-text [title]="fmt(priceLabsCents)" class="rc-running__amount" />
              <tidy-text [helper]="'After suggested price'" class="rc-running__detail" />
            </div>
          } @else {
            <div class="rc-running">
              <tidy-text [helper]="'No suggested price for this date yet.'" class="rc-running__detail" />
            </div>
          }
        </div>
      </article>

      <!-- Rank Tracker adjustment -->
      <article class="rc-layer" [attr.data-layer]="cardNum.rank"
        [class.rc-layer--inactive]="isBlocked || hasOverride">
        <div class="rc-layer__head">
          <span class="rc-layer__num">{{ cardNum.rank }}</span>
          <tidy-text [header]="'Rank Tracker Adjustment'" class="rc-layer__name" />
          @if (!comingSoonLayers) {
            <tidy-small-button
              class="rc-icon-btn"
              [smallIconButton]="true"
              [image]="'assets/svg/settings-outline.svg'"
              (buttonClick)="openCustomize()" />
          }
        </div>
        @if (comingSoonLayers) {
          <div class="rc-layer__body">
            <tidy-text [helper]="'We&rsquo;ll do this 30 days after going live.'" />
          </div>
        } @else {
        <div class="rc-layer__body">
          <div class="rc-rank-table" [style.--platform-cols]="platforms.length">
            <div class="rc-rank-table__head">
              <tidy-text [body]="'Search Query'" class="bold" />
              @for (p of platforms; track p) {
                <tidy-text [body]="platformLabels[p]" class="bold" />
              }
            </div>
            @for (q of enabledQueries; track q.id) {
              <div class="rc-rank-table__row">
                <div class="rc-rank-table__q">
                  <tidy-text [body]="q.location" class="rc-rank-table__q-label" />
                  <tidy-text [helper]="q.adults + ('g · ' | translate) + q.nights + ('n · ' | translate) + q.leadTimeDays + ('d' | translate)" class="rc-rank-table__q-meta" />
                </div>
                @for (p of platforms; track p) {
                  @let cell = rankFor(q.id, p);
                  <div>
                    @if (cell && cell.position !== null) {
                      <span class="rc-rank-badge">#{{ cell.position }}</span>
                      <span class="rc-rank-page">p{{ cell.page }}</span>
                    } @else {
                      <span class="rc-rank-badge">N/R</span>
                    }
                  </div>
                }
              </div>
            }
            @if (enabledQueries.length === 0) {
              <div class="rc-rank-empty">
                <tidy-text [helper]="'No queries enabled. Click the gear above.'" />
              </div>
            }
          </div>

          @if (rankAdjustment) {
            <tidy-text [helper]="rankAdjustment.narrative" class="rc-rank-narrative" />
          }
          <div class="rc-running" [attr.data-sign]="rankAdjustment ? adjustmentClass(rankAdjustment.pct) : null">
            <div class="rc-running__left">
              @if (rankAdjustment) {
                <span class="rc-running__pct">
                  {{ rankAdjustment.pct > 0 ? '+' : '' }}{{ rankAdjustment.pct }}%
                </span>
                <span class="rc-running__arrow">→</span>
              }
              <tidy-text [title]="fmt(postRankCents)" class="rc-running__amount" />
            </div>
            <tidy-text [helper]="'After rank adjustment'" class="rc-running__detail" />
          </div>
        </div>
        }
      </article>

      <!-- Per-platform discounts -->
      <article class="rc-layer" [attr.data-layer]="cardNum.platform" [class.rc-layer--inactive]="isBlocked">
        <div class="rc-layer__head">
          <span class="rc-layer__num">{{ cardNum.platform }}</span>
          <tidy-text [header]="'Per-Platform Discounts'" class="rc-layer__name" />
        </div>
        <div class="rc-layer__body">
          <tidy-text
            [helper]="'Per-platform discounts mean we set the target price higher, then apply discounts, so you earn the target for the day while optimizing conversion.'" />
          @if (isRealMode && !hasPlatformRows) {
            <tidy-text
              [helper]="'Connect a listing to see per-platform discounts for this date.'" />
          } @else {
            <div class="rc-platform-rows">
              @for (p of platformPrices; track p.platform) {
                <div class="rc-platform-row" [attr.data-discount]="p.discountPct > 0 ? 'on' : 'off'">
                  <div class="rc-platform-row__left">
                    <span class="rc-platform-row__pct">−{{ p.discountPct }}%</span>
                    @if (p.discountPct > 0 && p.reason) {
                      <tidy-text [helper]="'(' + p.reason.toLowerCase() + ')'" class="rc-platform-row__reason" />
                    }
                    <span class="rc-platform-row__arrow">→</span>
                    <tidy-text [body]="fmt(p.finalCents)" class="rc-platform-row__amount" />
                  </div>
                  <div class="rc-platform-row__right">
                    <tidy-text [helper]="p.label" class="rc-platform-row__name" />
                    @if (p.status | platformSyncNote; as note) {
                      <tidy-text [helper]="note" class="rc-platform-row__sync" />
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </article>

      <!-- Minimum-stay optimization. Unlike layers 3 & 6, this one is live for
           real accounts — the per-date min-stay override writes through
           address-settings-by-date (gotidy #8271). The surrounding-window viz is
           mock-only, so it simply doesn't render when there's no window. -->
      <article class="rc-layer" [attr.data-layer]="cardNum.stay"
        [class.rc-layer--inactive]="isBlocked">
        <div class="rc-layer__head">
          <span class="rc-layer__num">{{ cardNum.stay }}</span>
          <tidy-text [header]="'Minimum Stay Optimization'" class="rc-layer__name" />
        </div>
        <div class="rc-layer__body">
          <tidy-text
            [body]="'We adjust rates and stay limits to maximize RevPAR for the period — reducing the probability of unbookable holes in the schedule.'" />

          @if (surroundingWindow) {
            <div class="rc-window">
              <div class="rc-window__head">
                <tidy-text [body]="'Booking window around this date'" class="rc-window__title bold" />
                <span class="rc-window__legend">
                  <span class="rc-window__legend-item rc-window__legend-item--open">
                    <tidy-text [helper]="'open'" />
                  </span>
                  <span class="rc-window__legend-item rc-window__legend-item--gap">
                    <tidy-text [helper]="'fillable gap'" />
                  </span>
                  <span class="rc-window__legend-item rc-window__legend-item--booked">
                    <tidy-text [helper]="'booked'" />
                  </span>
                </span>
              </div>
              <div class="rc-window__strip" [style.--cols]="surroundingWindow.nights.length">
                @for (n of surroundingWindow.nights; track n.date) {
                  <div
                    class="rc-window__night"
                    [class.rc-window__night--booked]="n.booked"
                    [class.rc-window__night--gap]="n.inGap"
                    [class.rc-window__night--selected]="n.isSelected"
                    [style.--glow]="n.glow">
                    <span class="rc-window__dow">{{ n.dowLabel }}</span>
                    <span class="rc-window__num">{{ n.dayNum }}</span>
                  </div>
                }
              </div>
              <tidy-text [body]="surroundingWindow.narrative" class="rc-window__caption" />
            </div>
          }

          <div class="rc-stay-overrides-group">
            <tidy-checkbox
              class="rc-stay-override-toggle"
              [initValue]="showStayOverride"
              [(checked)]="showStayOverride"
              (checkedChange)="onShowStayOverrideChange($event)"
              [items]="stayOverrideToggleItems" />

            @if (showStayOverride) {
              <form [formGroup]="stayForm" class="rc-stay-overrides">
                <tidy-input
                  [label]="'Min nights'"
                  [type]="'number'"
                  [form]="stayForm.controls.minNights"
                  formControlName="minNights" />
              </form>
            }
          </div>
        </div>
      </article>

      <!-- Listing optimization -->
      @if (comingSoonLayers) {
        <article class="rc-layer" [attr.data-layer]="cardNum.listing">
          <div class="rc-layer__head">
            <span class="rc-layer__num">{{ cardNum.listing }}</span>
            <tidy-text [header]="'Listing Optimization'" class="rc-layer__name" />
          </div>
          <div class="rc-layer__body">
            <tidy-text [helper]="'We&rsquo;ll do this 30 days after going live.'" />
          </div>
        </article>
      } @else if (listingOpt) {
        <article class="rc-layer" [attr.data-layer]="cardNum.listing">
          <div class="rc-layer__head">
            <span class="rc-layer__num">{{ cardNum.listing }}</span>
            <tidy-text [header]="'Listing Optimization'" class="rc-layer__name" />
          </div>
          <div class="rc-layer__body">
            <ul class="rc-listing-changes">
              @for (c of listingOpt.changes; track $index) {
                <li class="rc-listing-change">
                  <span class="rc-listing-change__kind" [attr.data-kind]="c.kind">{{ c.kind }}</span>
                  <tidy-text [body]="c.summary" class="rc-listing-change__summary" />
                  <tidy-text [helper]="c.whenLabel" class="rc-listing-change__when" />
                </li>
              }
            </ul>
          </div>
        </article>
      }
    </section>
  } @else {
    <section class="rc-section">
      <div class="rc-empty">
        <tidy-text [helper]="'This night is already booked. Booked nights skip the optimization pipeline.'" />
      </div>
    </section>
  }
  }
  }
</div>

<!-- Search-term customization sub-modal (overlay inside the dialog) -->
@if (customizeOpen) {
  <div class="rc-popup-backdrop rc-popup-backdrop--nested" (click)="closeCustomize()">
    <div class="rc-popup rc-popup--narrow" (click)="$event.stopPropagation()">
      <header class="rc-popup__head">
        <div class="rc-popup__head-text">
          <tidy-text [header]="'Tracked search queries'" />
          <tidy-text [helper]="'Toggle which queries we monitor for rank.'" />
        </div>
        <img class="rc-popup__close" src="assets/img/white-close.svg" alt="Close" (click)="closeCustomize()" />
      </header>

      <div class="rc-query-list">
        @for (q of trackedQueries; track q.id) {
          <label class="rc-query-row">
            <tidy-checkbox
              [(checked)]="q.enabled"
              [items]="trackedQueryCheckboxItems" />
            <span class="rc-query-row__main">
              <tidy-text [body]="q.label" class="rc-query-row__label" />
              <tidy-text [helper]="q.location + ' · ' + q.adults + (' guests · ' | translate) + q.nights + (' nights · ' | translate) + q.leadTimeDays + ('d lead' | translate)" class="rc-query-row__meta" />
            </span>
          </label>
        }
      </div>

      <div class="rc-popup__actions">
        <tidy-button [text]="'+ Add query'" class="secondary" />
        <tidy-button [text]="'Done'" [action]="closeCustomize.bind(this)" class="primary" />
      </div>
    </div>
  </div>
}
</ng-template>

<!-- Desktop mounts day-detail inside the right-side-panel dialog, which
     already provides its own scrollable container. Mobile is a standalone
     route with no surrounding scroll, so wrap the body in app-scroll-wrapper
     to get an ion-content scroll surface (mirrors the booking-settings
     pattern). -->
@if (windowService.isDesktopRes) {
  <ng-container *ngTemplateOutlet="ddContent" />
} @else {
  <app-scroll-wrapper [isfullWidthContent]="true" [templateContent]="ddContent" />
}
